import React, { useRef, useState } from "react";
import type { MetadataKey } from "../types";
import { FilterButton } from "./FilterButton";
import { useClickOutside } from "../../../hooks/useClickOutside";
import { TagIcon } from "@heroicons/react/20/solid";

interface GroupBySelectorProps {
  metadataKeys: MetadataKey[];
  groupByKey?: string;
  onChange: (key?: string) => void;
}

/**
 * Single-select control choosing the tag key to group the insights by.
 * Immediate-apply (no apply/cancel); the "None" row clears grouping.
 */
export function GroupBySelector({
  metadataKeys,
  groupByKey,
  onChange,
}: GroupBySelectorProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useClickOutside(containerRef, () => setIsExpanded(false), isExpanded);

  const select = (key?: string) => {
    onChange(key);
    setIsExpanded(false);
  };

  const rowClasses = (active: boolean) =>
    `block w-full text-left px-3 py-2 rounded text-sm cursor-pointer hover:bg-accent ${
      active ? "bg-primary/10 text-primary font-medium" : "text-muted-foreground"
    }`;

  return (
    <div ref={containerRef} className="relative">
      <FilterButton
        label="Group by"
        isActive={!!groupByKey}
        isExpanded={isExpanded}
        onClick={() => setIsExpanded((v) => !v)}
        onRemove={groupByKey ? () => onChange(undefined) : undefined}
        icon={<TagIcon className="h-4 w-4" />}
        badge={groupByKey}
      />

      {isExpanded && (
        <div className="absolute left-0 top-full mt-2 bg-card border border-border rounded-lg shadow-lg p-2 z-10 min-w-[220px] max-h-72 overflow-y-auto">
          <button
            type="button"
            className={rowClasses(!groupByKey)}
            onClick={() => select(undefined)}
          >
            None
          </button>
          {metadataKeys.length === 0 ? (
            <div className="text-sm text-muted-foreground px-3 py-2">
              No tags available
            </div>
          ) : (
            metadataKeys.map((k) => (
              <button
                type="button"
                key={k.key}
                className={rowClasses(groupByKey === k.key)}
                onClick={() => select(k.key)}
              >
                {k.key}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
